import {yupResolver} from "@hookform/resolvers/yup";
import {FormProvider, useForm} from "react-hook-form";
import {useEffect, useRef} from "react";
import {ProfileFormValues, profileSchema} from "./schema-validation";
import {ProfilePublicDisplayNameFormField} from "./form-fields/public-display-name";
import {Button, Col, Row, Space} from "antd";
import {ProfilePublicUsernameFormField} from "./form-fields/username";
import {ProfileCountryFormField} from "./form-fields/country";
import {ProfileShortBioFormField} from "./form-fields/short-bio";
import {ProfileShortDescriptionFormField} from "./form-fields/short-description";
import {ProfileSocialLinksFormField} from "./form-fields/social-links";
import {ProfileLastNameFormField} from "./form-fields/last-name";
import {ProfileFirstNameFormField} from "./form-fields/first-name";
import {useGetProfileContext} from "../../../../context/profile/profile-context";
import {buildProfileSocialsObject} from "../../../../utils/profile";
import {ProfileAvatarFormFiled} from "./form-fields/avatar";
import {ProfileCoverImageFormField} from "./form-fields/cover-image";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {useUpdateUserProfileHook} from "../hooks/update-user-profile";
import {useLegacyDisplayNameStatus} from "../../../../features/profile/hooks/useProfile";

export function ProfileForm() {
  const {state: profileState} = useGetProfileContext();
  const {isUpdatingUserProfile, updateUserProfile} = useUpdateUserProfileHook();
  const {refetch: refetchDisplayNameStatus} = useLegacyDisplayNameStatus();

  const usernameString =
    profileState.profileData.username &&
    profileState.profileData.username.split("https://www.coachli.co/");

  const formMethods = useForm<ProfileFormValues>({
    resolver: yupResolver(profileSchema),
    mode: "onChange",
    defaultValues: {
      country: profileState.profileData.country,
      displayName: profileState.profileData.display_name || "",
      emailAddress: profileState.profileData.email,
      firstName: profileState.profileData.firstName,
      lastName: profileState.profileData.lastName,
      shortBio: profileState.profileData.shortBio,
      shortDescription: profileState.profileData.description,
      socialLinks: [...buildProfileSocialsObject(profileState.profileData)],
      username: usernameString ? usernameString[0] : "",
    },
  });

  // Sync form with server data after a successful mutation + refetch.
  // Without this, the form keeps stale defaultValues and subsequent
  // updates lose fields that weren't touched.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const pd = profileState.profileData;
    const uStr = pd.username && pd.username.split("https://www.coachli.co/");
    formMethods.reset({
      country: pd.country,
      displayName: pd.display_name || "",
      emailAddress: pd.email,
      firstName: pd.firstName,
      lastName: pd.lastName,
      shortBio: pd.shortBio,
      shortDescription: pd.description,
      socialLinks: [...buildProfileSocialsObject(pd)],
      username: uStr ? uStr[0] : "",
    });
  }, [profileState.profileData]); // eslint-disable-line react-hooks/exhaustive-deps

  function onSubmit(values: ProfileFormValues) {
    updateUserProfile({
      ...values,
      how_did_you_find_us: profileState.profileData.how_did_you_find_us ?? "",
    });

    refetchDisplayNameStatus({throwOnError: false});
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} autoComplete="off">
        <Space direction="vertical" size={24} style={{display: "flex"}}>
          <ProfileCoverImageFormField />
          <ProfileAvatarFormFiled />
          <ProfilePublicDisplayNameFormField
            firstName={profileState.profileData.firstName}
            lastName={profileState.profileData.lastName}
          />
          <ProfilePublicUsernameFormField />
          <Row gutter={[16, 29]}>
            <Col span={24} md={12}>
              <ProfileFirstNameFormField />
            </Col>
            <Col span={24} md={12}>
              <ProfileLastNameFormField />
            </Col>
          </Row>
          <ProfileCountryFormField />
          <ProfileShortBioFormField />
          <ProfileShortDescriptionFormField />
          <ProfileSocialLinksFormField />

          <Row gutter={[0, 16]}>
            <Col span={24} sm={12}>
              <StyledServiceButtonWrapper>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  size="small"
                  disabled={
                    Object.keys(formMethods.formState.errors).length > 0 ||
                    Object.values(formMethods.watch()).length < 1
                  }
                  loading={isUpdatingUserProfile}
                >
                  Update profile
                </Button>
                <Button
                  onClick={() => formMethods.reset()}
                  block
                  size="small"
                  type="default"
                >
                  Discard changes
                </Button>
              </StyledServiceButtonWrapper>
            </Col>
          </Row>
        </Space>
      </form>
    </FormProvider>
  );
}
